import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  ClipboardCheck, Clock, AlertTriangle, Loader2, CheckCircle2, XCircle, FileText,
} from 'lucide-react';
import PageShell from '../../../components/leaves/PageShell';
import { ReturnToWorkRequestView } from '../../../models/attendance';
import * as LeavesApi from '../../../api/Attendance/leaves';
import { getFullUrl } from '../../../utils/url';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import { appDate } from '../../../utils/appDateFormat';

interface ReturnToWorkProps {
  userId: string;
  company: { id: number };
  role: string;
}

type TabId = 'team-rtw' | 'my-rtw';

const statusConfig: Record<string, { label: string; bgClass: string; dotClass: string; icon: React.ElementType }> = {
  Pending: { label: 'Pending', bgClass: 'bg-amber-100 text-amber-800', dotClass: 'bg-amber-500', icon: Clock },
  DocumentsUploaded: { label: 'DocumentsUploaded', bgClass: 'bg-blue-100 text-blue-800', dotClass: 'bg-blue-500', icon: FileText },
  Completed: { label: 'Completed', bgClass: 'bg-green-100 text-green-800', dotClass: 'bg-green-500', icon: CheckCircle2 },
  Waived: { label: 'Waived', bgClass: 'bg-gray-100 text-gray-600', dotClass: 'bg-gray-400', icon: XCircle },
};

function formatDate(dateStr: string): string {
  return appDate(new Date(dateStr));
}

function isOverdue(dueDate: string, status: string): boolean {
  if (status === 'Completed' || status === 'Waived') return false;
  return new Date(dueDate) < new Date();
}

const ReturnToWork: React.FC<ReturnToWorkProps> = ({ role }) => {
  const { t } = useTranslation(['leaves', 'common']);
  const isManagerOrHr = ['Manager', 'Hr', 'Chief'].includes(role);

  const [activeTab, setActiveTab] = useState<TabId>(isManagerOrHr ? 'team-rtw' : 'my-rtw');
  const [teamRequests, setTeamRequests] = useState<ReturnToWorkRequestView[]>([]);
  const [myRequests, setMyRequests] = useState<ReturnToWorkRequestView[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [notesModal, setNotesModal] = useState<{ rtwId: number; currentNotes: string } | null>(null);
  const [notesText, setNotesText] = useState('');
  const [signOffModal, setSignOffModal] = useState<{ rtw: ReturnToWorkRequestView; action: 'signoff' | 'waive' } | null>(null);
  const [managerNotes, setManagerNotes] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [teamRes, myRes] = await Promise.all([
        isManagerOrHr ? LeavesApi.getReturnToWorkRequests() : Promise.resolve({ errors: [], payload: [] as ReturnToWorkRequestView[] }),
        LeavesApi.getMyReturnToWorkRequests(),
      ]);
      if (teamRes.errors.length === 0) setTeamRequests(teamRes.payload);
      if (myRes.errors.length === 0) setMyRequests(myRes.payload);
    } catch {
      setToast({ message: t('rtwLoadError'), type: 'error' });
    }
    setLoading(false);
  }, [isManagerOrHr, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleSubmitNotes = async () => {
    if (!notesModal) return;
    setActionLoading(notesModal.rtwId);
    try {
      const res = await LeavesApi.updateReturnToWorkNotes(notesModal.rtwId, notesText);
      if (res.errors.length === 0) {
        setToast({ message: t('rtwNotesUpdated'), type: 'success' });
        setNotesModal(null);
        fetchData();
      } else {
        setToast({ message: getApiErrorMessage(res, t, t('Error')), type: 'error' });
      }
    } catch (err) {
      setToast({ message: getApiErrorMessage(err, t, t('Error')), type: 'error' });
    }
    setActionLoading(null);
  };

  const handleSignOffOrWaive = async () => {
    if (!signOffModal) return;
    setActionLoading(signOffModal.rtw.id);
    try {
      const res = signOffModal.action === 'signoff'
        ? await LeavesApi.signOffReturnToWork(signOffModal.rtw.id, managerNotes)
        : await LeavesApi.waiveReturnToWork(signOffModal.rtw.id, managerNotes);
      if (res.errors.length === 0) {
        setToast({
          message: signOffModal.action === 'signoff' ? t('rtwSignedOff') : t('rtwWaived'),
          type: 'success',
        });
        setSignOffModal(null);
        setManagerNotes('');
        fetchData();
      } else {
        setToast({ message: getApiErrorMessage(res, t, t('Error')), type: 'error' });
      }
    } catch (err) {
      setToast({ message: getApiErrorMessage(err, t, t('Error')), type: 'error' });
    }
    setActionLoading(null);
  };

  const currentList = activeTab === 'team-rtw' ? teamRequests : myRequests;
  const pendingCount = teamRequests.filter(r => r.status !== 'Completed' && r.status !== 'Waived').length;
  const overdueCount = teamRequests.filter(r => r.status !== 'Completed' && r.status !== 'Waived' && new Date(r.dueDate) < new Date()).length;

  return (
    <PageShell
      title={t('Return to Work')}
      description={t('rtwDescription')}
      icon={ClipboardCheck}
      accentColor="emerald"
      maxWidth="7xl"
      stats={[
        { label: t('Team RTW Requests'), value: teamRequests.length },
        { label: t('translation:pending', 'Pending'), value: pendingCount, color: 'text-amber-600' },
        { label: t('Overdue'), value: overdueCount, color: 'text-red-600' },
        { label: t('My RTW Requests'), value: myRequests.length },
      ]}
      className="min-h-screen bg-background"
    >
      {/* Tabs */}
      <div className="pt-6">
        <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1 w-fit">
          {isManagerOrHr && (
            <button
              onClick={() => setActiveTab('team-rtw')}
              data-testid="tab-team-rtw"
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
                activeTab === 'team-rtw' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('Team RTW Requests')}
              {pendingCount > 0 && (
                <span className="bg-amber-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                  {pendingCount}
                </span>
              )}
            </button>
          )}
          <button
            onClick={() => setActiveTab('my-rtw')}
            data-testid="tab-my-rtw"
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'my-rtw' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('My RTW Requests')}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="py-6">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 size={32} className="animate-spin text-muted-foreground" />
          </div>
        ) : currentList.length === 0 ? (
          <div className="text-center py-24">
            <CheckCircle2 size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium text-foreground mb-2">{t('rtwNoRequests')}</h3>
            <p className="text-muted-foreground">{t('rtwNoRequestsMessage')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {currentList.map((rtw) => {
              const config = statusConfig[rtw.status] || statusConfig.Pending;
              const overdue = isOverdue(rtw.dueDate, rtw.status);
              const StatusIcon = config.icon;

              return (
                <div
                  key={rtw.id}
                  data-testid="rtw-card"
                  className={`bg-card border rounded-xl p-5 transition-shadow hover:shadow-md ${
                    overdue ? 'border-red-300' : 'border-border'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    {/* Left: Employee info & leave details */}
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center flex-shrink-0 text-white text-sm font-medium overflow-hidden">
                        {getFullUrl(rtw.userImageUrl) ? (
                          <img src={getFullUrl(rtw.userImageUrl)!} alt="" className="w-full h-full object-cover" />
                        ) : (
                          (rtw.userName || '?').charAt(0).toUpperCase()
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-foreground">{rtw.userName}</span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bgClass}`}>
                            <StatusIcon size={12} />
                            {t(config.label)}
                          </span>
                          {overdue && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <AlertTriangle size={12} />
                              {t('Overdue')}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          <span className="font-medium">{rtw.leaveType}</span>
                          <span className="mx-1">·</span>
                          {formatDate(rtw.leaveStartDate)} → {formatDate(rtw.leaveEndDate)}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {t('rtwDueDate')}: <span className={overdue ? 'text-red-600 font-medium' : ''}>{formatDate(rtw.dueDate)}</span>
                          {rtw.thresholdDays > 0 && (
                            <span className="ltr:ml-2 rtl:mr-2">· {t('rtwThreshold', { days: rtw.thresholdDays })}</span>
                          )}
                        </div>
                        {rtw.employeeNotes && (
                          <div className="mt-2 text-sm bg-blue-50 border border-blue-200 rounded-lg p-2">
                            <span className="font-medium text-blue-800">{t('Employee Notes')}:</span>{' '}
                            <span className="text-blue-700">{rtw.employeeNotes}</span>
                          </div>
                        )}
                        {rtw.managerNotes && (
                          <div className="mt-2 text-sm bg-green-50 border border-green-200 rounded-lg p-2">
                            <span className="font-medium text-green-800">{t('Manager Notes')}:</span>{' '}
                            <span className="text-green-700">{rtw.managerNotes}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {activeTab === 'my-rtw' && rtw.status !== 'Completed' && rtw.status !== 'Waived' && (
                        <button
                          onClick={() => {
                            setNotesModal({ rtwId: rtw.id, currentNotes: rtw.employeeNotes || '' });
                            setNotesText(rtw.employeeNotes || '');
                          }}
                          data-testid="rtw-upload-docs"
                          className="px-3 py-2 text-sm font-medium border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors"
                        >
                          {t('rtwSubmitDocuments')}
                        </button>
                      )}
                      {activeTab === 'team-rtw' && isManagerOrHr && rtw.status !== 'Completed' && rtw.status !== 'Waived' && (
                        <>
                          <button
                            onClick={() => { setSignOffModal({ rtw, action: 'signoff' }); setManagerNotes(''); }}
                            data-testid="rtw-signoff"
                            disabled={actionLoading === rtw.id}
                            className="px-3 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                          >
                            {t('rtwSignOff')}
                          </button>
                          <button
                            onClick={() => { setSignOffModal({ rtw, action: 'waive' }); setManagerNotes(''); }}
                            data-testid="rtw-waive"
                            disabled={actionLoading === rtw.id}
                            className="px-3 py-2 text-sm font-medium border border-border text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                          >
                            {t('rtwWaive')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Employee Notes Modal */}
      {notesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setNotesModal(null)}>
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-foreground mb-4">{t('rtwSubmitDocuments')}</h3>
            <p className="text-sm text-muted-foreground mb-3">{t('rtwNotesHint')}</p>
            <textarea
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              data-testid="rtw-notes-textarea"
              rows={4}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              placeholder={t('rtwNotesPlaceholder')}
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setNotesModal(null)}
                data-testid="leave-rtw-notes-cancel"
                className="px-4 py-2 text-sm font-medium border border-border rounded-lg hover:bg-muted"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={handleSubmitNotes}
                disabled={actionLoading === notesModal.rtwId}
                data-testid="leave-rtw-notes-submit"
                className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {actionLoading === notesModal.rtwId ? <Loader2 size={16} className="animate-spin" /> : t('Submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manager Sign-Off / Waive Modal */}
      {signOffModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSignOffModal(null)}>
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {signOffModal.action === 'signoff' ? t('rtwConfirmSignOff') : t('rtwConfirmWaive')}
            </h3>
            <p className="text-sm text-muted-foreground mb-1">
              {t('Employee')}: <span className="font-medium text-foreground">{signOffModal.rtw.userName}</span>
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              {signOffModal.rtw.leaveType} · {formatDate(signOffModal.rtw.leaveStartDate)} → {formatDate(signOffModal.rtw.leaveEndDate)}
            </p>
            <textarea
              value={managerNotes}
              onChange={(e) => setManagerNotes(e.target.value)}
              data-testid="rtw-manager-notes"
              rows={3}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              placeholder={t('rtwManagerNotesPlaceholder')}
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setSignOffModal(null)}
                data-testid="leave-rtw-signoff-cancel"
                className="px-4 py-2 text-sm font-medium border border-border rounded-lg hover:bg-muted"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={handleSignOffOrWaive}
                disabled={actionLoading === signOffModal.rtw.id}
                data-testid="leave-rtw-signoff-confirm"
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 ${
                  signOffModal.action === 'signoff'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-gray-600 hover:bg-gray-700'
                }`}
              >
                {actionLoading === signOffModal.rtw.id
                  ? <Loader2 size={16} className="animate-spin" />
                  : signOffModal.action === 'signoff' ? t('rtwSignOff') : t('rtwWaive')
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.message}
        </div>
      )}
    </PageShell>
  );
};

const mapStateToProps = (state: any) => ({
  userId: state.user.userId,
  company: state.user.company,
  role: state.user.role,
});

export default connect(mapStateToProps)(ReturnToWork);
